/* ──────────────────────────────────────────────────────────────
   src/components/ProtectedRoute.tsx
   ▸ Wrapper para rutas privadas
     – Mientras arranca (bootstrapped=false) → Loader
     – Sin sesión  → /login
     – mustChangePwd=true → /change-password
   ▸ Modo de uso:
       1) Con sub-rutas
            <Route element={<ProtectedRoute />}> … </Route>
            (renderiza <Outlet /> internamente)
       2) Como envoltorio de un componente concreto
            <ProtectedRoute><MiComponente/></ProtectedRoute>
   ────────────────────────────────────────────────────────────── */

import { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface Props {
  /** Opción 2 – envolver un componente concreto */
  children?: ReactNode;
}

/** Loader simple mientras se rehidrata la sesión */
function Bootloader() {
  return (
    <div
      style={{
        padding: 24,
        display: 'grid',
        placeItems: 'center',
        minHeight: '40vh',
        fontWeight: 600,
      }}
    >
      Cargando sesión…
    </div>
  );
}

export default function ProtectedRoute({ children }: Props) {
  const { user, bootstrapped } = useAuth();
  const location = useLocation();

  /* ─── 0) NO decidas hasta que termine el bootstrap ─── */
  if (!bootstrapped) {
    return <Bootloader />;
  }

  /* ─── 1) sin sesión → /login ─── */
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  /* ─── 2) primer acceso → /change-password ─── */
  if (user.mustChangePwd && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  /* ─── 3) OK ─── */
  // · Si hay children → modo 2 (wrapper)
  // · Si no → modo 1 (sub-rutas) y delegamos en <Outlet/>
  return children ? <>{children}</> : <Outlet />;
}
